import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '@stores/gameStore';

interface CloudProps {
  position: [number, number, number];
  scale?: number;
  speed?: number;
}

/**
 * Individual cloud made of soft spheres
 */
function Cloud({ position, scale = 1, speed = 0.5 }: CloudProps): React.JSX.Element {
  const groupRef = useRef<THREE.Group>(null);
  const initialX = useRef(position[0]);

  // Generate random cloud shape
  const spheres = useMemo(() => {
    const count = 5 + Math.floor(Math.random() * 4);
    return Array.from({ length: count }, (_, i) => ({
      key: i,
      position: [
        (Math.random() - 0.5) * 3,
        (Math.random() - 0.5) * 1,
        (Math.random() - 0.5) * 2,
      ] as [number, number, number],
      radius: 0.5 + Math.random() * 0.8,
    }));
  }, []);

  useFrame(() => {
    if (groupRef.current) {
      // Drift clouds slowly to the left
      groupRef.current.position.x -= speed * 0.01;

      // Reset position when cloud moves too far left
      if (groupRef.current.position.x < -50) {
        groupRef.current.position.x = initialX.current + 80;
      }
    }
  });

  return (
    <group ref={groupRef} position={position} scale={scale}>
      {spheres.map((sphere) => (
        <mesh key={sphere.key} position={sphere.position} castShadow={false}>
          <sphereGeometry args={[sphere.radius, 16, 16]} />
          <meshStandardMaterial
            color="#ffffff"
            transparent
            opacity={0.9}
            roughness={1}
            metalness={0}
          />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Cloud layer component
 * Multiple clouds at varying heights and depths
 */
export function Clouds(): React.JSX.Element {
  const { multiplier } = useGameStore();

  // Generate cloud positions
  const cloudConfigs = useMemo(() => {
    return [
      // Background clouds (far, big, slow)
      { position: [-20, 12, -30] as [number, number, number], scale: 2.5, speed: 0.2 },
      { position: [10, 15, -35] as [number, number, number], scale: 3, speed: 0.15 },
      { position: [40, 10, -40] as [number, number, number], scale: 2.8, speed: 0.18 },
      { position: [-30, 18, -45] as [number, number, number], scale: 3.5, speed: 0.12 },

      // Mid-layer clouds
      { position: [-15, 8, -15] as [number, number, number], scale: 1.5, speed: 0.4 },
      { position: [15, 12, -20] as [number, number, number], scale: 2, speed: 0.35 },
      { position: [35, 6, -18] as [number, number, number], scale: 1.8, speed: 0.38 },
      { position: [-25, 14, -22] as [number, number, number], scale: 2.2, speed: 0.3 },

      // Foreground clouds (close, small, fast - but careful to not obscure plane)
      { position: [-10, 20, -8] as [number, number, number], scale: 1, speed: 0.6 },
      { position: [25, 18, -10] as [number, number, number], scale: 1.2, speed: 0.55 },
      { position: [5, 22, -12] as [number, number, number], scale: 0.8, speed: 0.65 },
    ];
  }, []);

  // Adjust cloud layer height based on multiplier (plane climbs through clouds)
  const cloudLayerOffset = useMemo(() => {
    const progress = Math.min((multiplier - 1) / 5, 1);
    return -progress * 8; // Clouds appear to move down as plane climbs
  }, [multiplier]);

  return (
    <group position={[0, cloudLayerOffset, 0]}>
      {cloudConfigs.map((config, index) => (
        <Cloud
          key={index}
          position={config.position}
          scale={config.scale}
          speed={config.speed}
        />
      ))}
    </group>
  );
}
